import axios from "axios";
import {
  Button,
  ButtonGroup,
  TextField,
  IconButton,
  LinearProgress,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Box
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FormProvider, useForm, useWatch, useFieldArray, useFormContext } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";

import { FormField, showSection } from "./FormRender";
import { AssetsData } from "../AssetsProvider";
import {
  APP_BASE_URL,
  GET_DEVICE_TYPE_ASSET,
  GET_ASSET_FORM,
  ASSET_FORM,
  ASSET_ADDITIONAL_INFO,
  APP_BASE_URL_ALARM,
  GENERIC_ATTACHMENT,
  GET_UNIQUE_ASSET_CODE,
} from "../../../Common/EndPoints";
import { getGeneralInfoForm, notifyAssetUpdate } from "./assetRegisterUtils";
import AttachPopUp from "./AttachPopUp";
import AfterSubmitPopup from "./AfterSubmitPopup";
import "./AssetRegister.scss";
import { getParsedLocalStorage } from "../../../Utils/storage-utils";

// Constants for auto-save
const DRAFT_STORAGE_KEY = "assetRegisterDraft";
const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

const checkKeysValueInObj = (keysArray, obj) => {
  for (let i = 0; i < keysArray.length; i++) {
    if (!obj[keysArray[i]]) {
      return false;
    }
  }
  return true;
};

// Hook for unsaved changes warning
const useUnsavedChangesWarning = (hasUnsavedChanges) => {
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);
};

// Hook for keyboard shortcuts
const useKeyboardShortcuts = (onSave) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        onSave();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onSave]);
};

// Hook for debounced asset code fetching
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Loading skeleton component
const FormLoadingSkeleton = () => (
  <div className="assets__asset-register__form">
    {[...Array(8)].map((_, i) => (
      <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
    ))}
  </div>
);

// Progress indicator component
const FormProgressIndicator = ({ form, formValues, requiredFields }) => {
  const filledRequired = requiredFields.filter((key) => formValues?.[key]).length;
  const progress = requiredFields.length > 0 ? (filledRequired / requiredFields.length) * 100 : 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <span className="text-sm text-gray-600">
          Form Progress: {filledRequired} of {requiredFields.length} required fields
        </span>
        {progress === 100 && (
          <Chip
            icon={<CheckCircleIcon />}
            label="All required fields filled"
            color="success"
            size="small"
          />
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: "#e0e0e0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: progress === 100 ? "#4caf50" : "#1976d2",
          },
        }}
      />
    </Box>
  );
};

// Collapsible section component
const CollapsibleSection = ({ title, children, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="collapsible-section">
      <div
        className="collapsible-section__header"
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          padding: "0.75rem 1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: expanded ? "8px 8px 0 0" : "8px",
          border: "1px solid #e0e0e0",
          marginTop: "1rem",
        }}
      >
        <h4 style={{ margin: 0, fontWeight: 600 }}>{title}</h4>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </div>
      {expanded && (
        <div
          className="collapsible-section__content"
          style={{
            padding: "1rem",
            border: "1px solid #e0e0e0",
            borderTop: "none",
            borderRadius: "0 0 8px 8px",
            backgroundColor: "#fafafa",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

const MiscellaneousFields = () => {
  const { control, register } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "miscellaneous",
  });

  return (
    <div className="mt-4 border-t pt-4">
      <div className="mb-2 font-bold">Miscellaneous</div>
      <div className="flex flex-col gap-2 mb-2">
        {fields.map((item, index) => (
          <div key={item.id} className="flex gap-2 items-center">
            <TextField
              {...register(`miscellaneous.${index}.key`)}
              label="Key"
              variant="outlined"
              size="small"
              required
            />
            <TextField
              {...register(`miscellaneous.${index}.value`)}
              label="Value"
              variant="outlined"
              size="small"
              required
            />
            <IconButton onClick={() => remove(index)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </div>
        ))}
      </div>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => append({ key: "", value: "" })}
        size="small"
      >
        Add Field
      </Button>
    </div>
  );
};

export const Assets = () => {
  const location = useLocation();
  const defaultState = location?.state?.d;
  const defaultValues = defaultState ?? {};
  const navigate = useNavigate();
  const [showAttachmentPopup, setShowAttachmentPopup] = useState(false);
  const [showAfterSubmissionPopup, setShowAfterSubmissionPopup] = useState(false);
  const { attachmentsData, DEVICE_ID, setDeviceId, uploadAttachments, setAttachmentsData, setFormId } =
    useContext(AssetsData);
  const editMode = useMemo(() => (DEVICE_ID || defaultState ? true : false), [DEVICE_ID, defaultState]);
  const generalInfoData = useRef({ id: "general_info", name: "General Info", data: [] });
  const [form, setForm] = useState(generalInfoData.data);
  const form_keys_map = useMemo(() => {
    const _keys = {};
    form?.forEach((field) => (_keys[field.key] = field));
    return _keys;
  }, [form]);
  const [subTabList, setSubTabList] = useState([]);
  const formsList = useMemo(() => [generalInfoData.current, ...subTabList], [subTabList]);
  const systems = getParsedLocalStorage("systems_list", []);
  const selectedSystem = systems.find((system) => system.id === defaultValues?.systemId);
  console.log({...defaultValues, system: selectedSystem?.code}, systems)
  const formMethods = useForm({ mode: "onSubmit", defaultValues: { ...defaultValues, system: selectedSystem?.code } });
  const deviceTypeCollectionMapIds = useRef(null);
  const [selectedTab, setSelectedTab] = useState("general_info");
  const formNavigation = useMemo(() => {
    let current_tab = null;
    let indexFound = 0;
    for (const form of formsList) {
      if (form.id === selectedTab) {
        current_tab = form;
        break;
      }
      indexFound++;
    }
    const nextFormIndex = indexFound + 1;
    const next_tab = nextFormIndex >= formsList.length ? null : formsList[nextFormIndex];
    return { current_tab, next_tab };
  }, [formsList, selectedTab]);
  const customerNameToId = useRef(null);
  const formValues = useRef();
  const responseId = useRef();

  // New state for improvements
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showDraftSnackbar, setShowDraftSnackbar] = useState(false);
  const [draftAvailable, setDraftAvailable] = useState(false);

  // Calculate required fields for progress indicator
  const requiredFields = useMemo(() => {
    return form?.filter((field) => field.required)?.map((field) => field.key) ?? [];
  }, [form]);

  // Watch all form values for progress and auto-save
  const watchedValues = useWatch({ control: formMethods.control });

  const { handleSubmit, control, reset, getValues } = formMethods;
  const gearTypeValue = useWatch({ name: "gearType", control });

  // Auto-save draft to localStorage
  const saveDraft = useCallback(() => {
    if (editMode) return; // Don't save drafts in edit mode
    const currentValues = getValues();
    const draftData = {
      values: currentValues,
      tab: selectedTab,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftData));
    setLastSaved(new Date());
    setHasUnsavedChanges(false);
  }, [getValues, selectedTab, editMode]);

  // Restore draft from localStorage
  const restoreDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      const { values } = JSON.parse(savedDraft);
      reset(values);
      setDraftAvailable(false);
      setShowDraftSnackbar(false);
      toast.success("Draft restored successfully");
    }
  }, [reset]);

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setDraftAvailable(false);
    setShowDraftSnackbar(false);
  }, []);

  // Use custom hooks
  useUnsavedChangesWarning(hasUnsavedChanges);
  useKeyboardShortcuts(saveDraft);

  // Track unsaved changes
  useEffect(() => {
    if (!isLoading && !editMode) {
      setHasUnsavedChanges(true);
    }
  }, [watchedValues, isLoading, editMode]);

  // Auto-save interval
  useEffect(() => {
    if (editMode) return;
    const interval = setInterval(() => {
      if (hasUnsavedChanges) {
        saveDraft();
      }
    }, AUTO_SAVE_INTERVAL);
    return () => clearInterval(interval);
  }, [hasUnsavedChanges, saveDraft, editMode]);

  // Check for existing draft on mount
  useEffect(() => {
    if (editMode) return;
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      const { timestamp } = JSON.parse(savedDraft);
      const savedDate = new Date(timestamp);
      const now = new Date();
      const hoursDiff = (now - savedDate) / (1000 * 60 * 60);
      // Only show draft if less than 24 hours old
      if (hoursDiff < 24) {
        setDraftAvailable(true);
        setShowDraftSnackbar(true);
      } else {
        clearDraft();
      }
    }
  }, [editMode, clearDraft]);

  function handleTabChange(tab) {
    setAttachmentsData([]);
    const _tab = tab === "additional_info" ? subTabList?.[0] : tab;
    if (selectedTab === _tab.id) return;
    formValues.current = { ...formValues.current, [selectedTab]: getValues() };
    setSelectedTab(_tab.id);
    setForm(_tab.data);
    if (_tab.id !== "general_info") {
      setFormId(_tab.id);
    } else {
      setFormId(null);
    }
    const storedValues = formValues.current[_tab.id];
    // console.log("stored values", storedValues);
    if (editMode && _tab.id !== "general_info" && !storedValues) {
      const url = `${APP_BASE_URL}${ASSET_ADDITIONAL_INFO}`;
      const form_id = _tab.id !== "general_info" ? _tab.id : null;
      const params = { device_id: DEVICE_ID, form_id };
      axios.get(url, { params }).then((res) => {
        const formResponseValues = res.data.data?.[0]?.data;
        // console.log("form response values", formResponseValues);
        responseId.current = res.data.data?.[0]?.id;
        reset(formResponseValues ?? storedValues);
      });
    } else {
      reset(storedValues ?? {});
    }
  }

  function handleCloseAttachmentPopup() {
    setShowAttachmentPopup(false);
  }

  function formSubmit(_values) {
    const values = {};
    form.forEach((field) => {
      if (field.type === "reactive-section" || field.type === "reactive_section") {
        const reactiveValue = field.reactiveValue;
        const parentKey = field.reactiveKey;
        const parentValues = _values[parentKey];
        if (showSection({ parentValues, reactiveValue })) {
          field.sectionFields.forEach((_field) => {
            if (_values[_field.key]) values[_field.key] = _values[_field.key];
          });
        }
        return;
      }
      if (field.type === "section") {
        field.sectionFields.forEach((_field) => {
          if (_values[_field.key]) values[_field.key] = _values[_field.key];
        });
        return;
      }
      if(field.type === "checkbox") {
        values[field.key] = _values[field.key]
        return;
      }
      if (_values[field.key]) values[field.key] = _values[field.key];
    });
    if (_values.miscellaneous) {
      const formKeys = form.map((field) => field.key);
      _values.miscellaneous.forEach((item) => {
        if (item.key && !formKeys.includes(item.key)) {
          values[item.key] = item.value;
        }
      });
    }
    if (selectedTab === "general_info") {
      const { zone, division, station, gear_name, lat, lon, gearType, isLogical, isMaintenanceEnable } = values;
      const name = `${zone}_${division}_${station}_${gear_name}`;
      const type_id = deviceTypeCollectionMapIds.current?.[gearType].type_id;
      const customer_id = customerNameToId.current[station]?.toString();

      const systems = getParsedLocalStorage("systems_list", []);
      const system = systems.find((system) => system.code === values.system);
      const params = { name, type_id, customer_id, lat, lon, softwareId: system?.id, isLogicalDevice: Boolean(isLogical), isMaintenanceEnable: Boolean(isMaintenanceEnable) };

      let apiRequest = axios.post;
      let url = `${APP_BASE_URL}${ASSET_FORM}`;
      if (editMode) {
        delete params.isMaintenanceEnable;
        apiRequest = axios.put;
        url += `/${DEVICE_ID}`;
      }

      apiRequest(url, values, { params }).then(
        (res) => {
          let deviceId = res.data.data.deviceId;
          setDeviceId(deviceId);
          // notifyAssetUpdate();
          if (attachmentsData?.length !== 0) {
            uploadAttachments(deviceId, attachmentsData, null).then((res) => {
              toast.success("Attachments Uploaded Successfully");
            });
          }
          // Clear draft after successful submission
          clearDraft();
          setHasUnsavedChanges(false);
          setShowAfterSubmissionPopup(true);
        },
        (err) => {
          const errorMessage = err?.response?.data?.error?.message ?? "Something went wrong";
          toast.error(errorMessage);
        },
      );
      return;
    }
    if (!DEVICE_ID) {
      toast.error("Please fill General Info Form first");
      return;
    }
    let url = `${APP_BASE_URL}${ASSET_ADDITIONAL_INFO}`;
    const device_id = DEVICE_ID;
    let userId = localStorage.getItem("userId");
    const created_by = userId;
    const form_id = selectedTab;
    let params = {};
    let apiRequest = axios.post;
    if (responseId.current) {
      apiRequest = axios.put;
      const id = responseId.current;
      const updated_by = userId;
      const status = "PENDING";
      url += "/" + id;
      params = { id, updated_by, status };
    } else {
      params = { device_id, created_by, form_id };
    }
    const formValues = { ...values };
    const promises = [];
    form.forEach((field) => {
      if (field.type === "attachment") {
        if (formValues[field.key]?.length > 0) {
          const files = [...formValues[field.key]];
          const uploadedFilesIds = [];
          const uploadFiles = new FormData();
          files.forEach((file) => {
            if (file instanceof File) {
              uploadFiles.append("files", file);
            } else {
              uploadedFilesIds.push(file);
            }
          });
          const user_id = localStorage.getItem("userId");
          const params = { entity_id: field.key, entity: "form", category: form_id, user_id };
          const promise = axios.post(APP_BASE_URL_ALARM + GENERIC_ATTACHMENT, uploadFiles, { params }).then((res) => {
            formValues[field.key] = [...uploadedFilesIds, ...res?.data?.map((file) => file.id)];
            return res.data;
          });
          promises.push(promise);
        }
      }
    });
    Promise.all(promises).then((attachments) => {
      // console.log("attachments", attachments);
      apiRequest(url, formValues, { params }).then(
        (res) => {
          if (attachmentsData?.length !== 0) {
            let deviceId = res.data.data.deviceId;
            uploadAttachments(deviceId, attachmentsData, responseId.current).then((res) => {
              toast.success("Attachments Uploaded Successfully");
            });
          }
          setShowAfterSubmissionPopup(true);
        },
        (err) => {
          const errorMessage = err?.response?.data?.error?.message ?? "Something went wrong";
          toast.error(errorMessage);
        },
      );
    });
  }

  useEffect(() => {
    setAttachmentsData([]);
    setIsLoading(true);
    const params = { with_map: true };
    axios.get(`${APP_BASE_URL}${GET_DEVICE_TYPE_ASSET}`, { params }).then(
      (res) => {
        const _mapIds = {};
        const deviceTypesList = res.data.data.map((_deviceType) => {
          _mapIds[_deviceType.label] = {
            type_id: _deviceType.typeid,
            collection_map_id: _deviceType?.collectionMaps?.[0]?.collectionmapid,
          };
          return _deviceType.label;
        });
        deviceTypeCollectionMapIds.current = _mapIds;
        let permissions = localStorage.getItem("permissions");
        if (permissions) permissions = JSON.parse(permissions);
        const { form, customer_name_to_id } = getGeneralInfoForm(deviceTypesList, permissions, editMode);
        customerNameToId.current = customer_name_to_id;
        generalInfoData.current.data = form;
        setForm(form);

        if (defaultValues?.additionaldetail) {
          const formKeys = form.map((field) => field.key);
          const miscData = [];
           Object.entries(defaultValues.additionaldetail).forEach(([key, value]) => {
            if (!formKeys.includes(key)) {
              miscData.push({ key, value });
            }
          });
          if (miscData.length > 0) {
            formMethods.setValue("miscellaneous", miscData);
          }
        }

        if (defaultState?.gearType) {
          setSubTabData(defaultState?.gearType);
          setDeviceId(defaultState?.deviceId);
        }
        setIsLoading(false);
      },
      (err) => {
        toast.error("Error Loading Form");
        setIsLoading(false);
      },
    );

    return () => {
      setAttachmentsData([]);
      setDeviceId(null);
    };
  }, []);

  useEffect(() => {
    if (!gearTypeValue || selectedTab !== "general_info") return;
    setSubTabData(gearTypeValue);
  }, [gearTypeValue]);

  function setSubTabData(_collection_map_id) {
    const collection_map_id = deviceTypeCollectionMapIds?.current?.[_collection_map_id]?.collection_map_id;
    if (!collection_map_id) {
      setSubTabList([]);
      return;
    }
    axios
      .get(`${APP_BASE_URL}${GET_ASSET_FORM}`, {
        params: { collection_map_id: collection_map_id, publish: true },
      })
      .then((res) => setSubTabList(res.data.data));
  }

  function handleNavigateToNextForm(status) {
    setShowAfterSubmissionPopup(false);
    if (status) {
      const _nextTab = formNavigation.next_tab;
      handleTabChange(_nextTab);
      return;
    }
    navigate("/assets");
  }

  const handleBlur = (fieldKey) => {
    const assetCodeDependentKeys = ["zone", "division", "station", "gear_name"];
    if (assetCodeDependentKeys.includes(fieldKey)) {
      const formValues = getValues();
      if (checkKeysValueInObj(assetCodeDependentKeys, formValues)) {
        const zoneCode = formValues["zone"];
        const divisionCode = formValues["division"];
        const stnCode = formValues["station"];
        const gearName = formValues["gear_name"];
        fetchAssetCode(zoneCode, divisionCode, stnCode, gearName);
      }
    }
  };
  const fetchAssetCode = (zoneCode, divisionCode, stnCode, gearName) => {
    let featchAssetCodeUrl = `${APP_BASE_URL}${GET_UNIQUE_ASSET_CODE}`;
    let params = { zoneCode, divisionCode, stnCode, gearName };
    axios.get(featchAssetCodeUrl, { params }).then((res) => {
      const uniqueAssetCode = res.data.data;
      formMethods.setValue("code", uniqueAssetCode);
    });
  };

  const formRender = form?.map((field) => <FormField handleBlur={handleBlur} key={field.key} fieldData={field} />);
  const providerProps = { form_keys_map, editMode };

  // Group form fields by sections for collapsible display
  const groupedFormFields = useMemo(() => {
    if (!form || form.length === 0) return { location: [], equipment: [], dates: [], other: [] };

    const locationKeys = ["system", "gearType", "zone", "division", "station", "code", "gear_name", "sub_type", "lat", "lon"];
    const equipmentKeys = ["make", "model", "serial", "connected_to", "position", "pole_km", "fms_link_id", "qrid"];
    const dateKeys = ["manufacture_date", "purchase_date", "installation_date", "warranty_expiry_date", "maintenance_start_date", "codal_life"];

    const grouped = {
      location: [],
      equipment: [],
      dates: [],
      other: [],
    };

    form.forEach((field) => {
      if (locationKeys.includes(field.key)) {
        grouped.location.push(field);
      } else if (equipmentKeys.includes(field.key)) {
        grouped.equipment.push(field);
      } else if (dateKeys.includes(field.key)) {
        grouped.dates.push(field);
      } else {
        grouped.other.push(field);
      }
    });

    return grouped;
  }, [form]);

  return (
    <>
      {/* Header with status indicators */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <h2 style={{ margin: 0 }}>{editMode ? "Edit Asset" : "Register New Asset"}</h2>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {lastSaved && (
            <Chip
              size="small"
              icon={<CheckCircleIcon />}
              label={`Saved ${lastSaved.toLocaleTimeString()}`}
              color="success"
              variant="outlined"
            />
          )}
          {hasUnsavedChanges && !editMode && (
            <Chip size="small" label="Unsaved changes" color="warning" variant="outlined" />
          )}
          {!editMode && (
            <Tooltip title="Save draft (Ctrl+S)">
              <IconButton onClick={saveDraft} color="primary" size="small">
                <SaveIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Tab Navigation */}
      <ButtonGroup
        variant="outlined"
        aria-label="outlined button group"
        style={{ display: "block", marginBottom: "1rem" }}
      >
        <Button
          variant={selectedTab === "general_info" ? "contained" : "outlined"}
          onClick={() => handleTabChange(generalInfoData.current)}
        >
          General Info
          {selectedTab === "general_info" && requiredFields.length > 0 && (
            <Chip
              size="small"
              label={`${requiredFields.filter((k) => watchedValues?.[k]).length}/${requiredFields.length}`}
              sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
              color={requiredFields.every((k) => watchedValues?.[k]) ? "success" : "default"}
            />
          )}
        </Button>
        {subTabList.length > 0 && (
          <Button
            variant={selectedTab !== "general_info" ? "contained" : "outlined"}
            onClick={() => handleTabChange("additional_info")}
          >
            Additional Info
          </Button>
        )}
      </ButtonGroup>

      {/* Sub-Tab Navigation */}
      <ButtonGroup
        variant="outlined"
        aria-label="outlined button group"
        style={{ display: "block", marginBottom: "1rem" }}
      >
        {selectedTab !== "general_info" &&
          subTabList.map((_subTab) => (
            <Button
              variant={selectedTab === _subTab.id ? "contained" : "outlined"}
              key={_subTab.id}
              onClick={() => handleTabChange(_subTab)}
            >
              {_subTab.name}
            </Button>
          ))}
      </ButtonGroup>

      {/* Progress Indicator */}
      {selectedTab === "general_info" && !isLoading && (
        <FormProgressIndicator
          form={form}
          formValues={watchedValues}
          requiredFields={requiredFields}
        />
      )}

      {/* Loading State */}
      {isLoading ? (
        <FormLoadingSkeleton />
      ) : (
        <FormProvider {...formMethods} {...providerProps}>
          <form onSubmit={handleSubmit(formSubmit)}>
            {/* Collapsible Sections for General Info */}
            {selectedTab === "general_info" ? (
              <div className="asset-register-sections">
                {groupedFormFields.location.length > 0 && (
                  <CollapsibleSection title="Location & Identity" defaultExpanded={true}>
                    <div className="assets__asset-register__form">
                      {groupedFormFields.location.map((field) => (
                        <FormField handleBlur={handleBlur} key={field.key} fieldData={field} />
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {groupedFormFields.equipment.length > 0 && (
                  <CollapsibleSection title="Equipment Details" defaultExpanded={true}>
                    <div className="assets__asset-register__form">
                      {groupedFormFields.equipment.map((field) => (
                        <FormField handleBlur={handleBlur} key={field.key} fieldData={field} />
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {groupedFormFields.dates.length > 0 && (
                  <CollapsibleSection title="Dates & Lifecycle" defaultExpanded={true}>
                    <div className="assets__asset-register__form">
                      {groupedFormFields.dates.map((field) => (
                        <FormField handleBlur={handleBlur} key={field.key} fieldData={field} />
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                {groupedFormFields.other.length > 0 && (
                  <CollapsibleSection title="Additional Information" defaultExpanded={true}>
                    <div className="assets__asset-register__form">
                      {groupedFormFields.other.map((field) => (
                        <FormField handleBlur={handleBlur} key={field.key} fieldData={field} />
                      ))}
                    </div>
                  </CollapsibleSection>
                )}

                <CollapsibleSection title="Miscellaneous Fields" defaultExpanded={false}>
                  <MiscellaneousFields />
                </CollapsibleSection>
              </div>
            ) : (
              <div className="assets__asset-register__form flex flex-col gap-2">
                {formRender}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between" style={{ marginTop: "1rem" }}>
              <div className="flex gap-2">
                <Button className="btn" variant="contained" onClick={() => setShowAttachmentPopup(true)}>
                  Attachments
                </Button>
                {!editMode && draftAvailable && (
                  <Tooltip title="Restore saved draft">
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestoreIcon />}
                      onClick={restoreDraft}
                    >
                      Restore Draft
                    </Button>
                  </Tooltip>
                )}
              </div>
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </div>
          </form>
        </FormProvider>
      )}

      {/* Attachment Popup */}
      {showAttachmentPopup && (
        <AttachPopUp
          open={true}
          label={"Add Attachments"}
          onClose={handleCloseAttachmentPopup}
          attachmentsEdit={false}
          inAdditionalForm={selectedTab !== "general_info"}
        />
      )}

      {/* After Submission Popup */}
      {showAfterSubmissionPopup && (
        <AfterSubmitPopup
          open={true}
          currentForm={formNavigation.current_tab?.name ?? null}
          nextForm={formNavigation.next_tab?.name ?? null}
          handleConfirMation={handleNavigateToNextForm}
        />
      )}

      {/* Draft Available Snackbar */}
      <Snackbar
        open={showDraftSnackbar}
        autoHideDuration={10000}
        onClose={() => setShowDraftSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowDraftSnackbar(false)}
          severity="info"
          sx={{ width: "100%" }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button color="inherit" size="small" onClick={restoreDraft}>
                Restore
              </Button>
              <Button color="inherit" size="small" onClick={clearDraft}>
                Discard
              </Button>
            </Box>
          }
        >
          You have a saved draft from a previous session.
        </Alert>
      </Snackbar>
    </>
  );
};
export default Assets;
